import { useEffect, useMemo, useRef } from 'react';
import { useShortcutRegistry } from '@/app/providers/ShortcutProvider';
import { v4 as uuidv4 } from 'uuid';

/**
 * useKeyboardNavigation Hook
 * Refactored to use a centralized Global Shortcut Registry.
 *
 * @param {Object} keyMap - An object mapping keyboard keys to callback functions.
 * @param {Object} options - Configuration options.
 * @param {boolean} options.enabled - Whether the listener is active (default: true).
 * @param {boolean} options.preventDefault - Whether to call e.preventDefault() (default: true).
 * @param {boolean} options.allowWhileTyping - Whether to allow these shortcuts even if an input is focused (default: false).
 */
const useKeyboardNavigation = (keyMap = {}, options = {}) => {
   const { enabled = true, preventDefault = true, allowWhileTyping = false } = options;
   const { register, unregister } = useShortcutRegistry() || {};

   // Generate a stable ID for this hook instance to manage its place in the stack
   const idRef = useRef(useMemo(() => `kbd-${uuidv4()}`, []));

   useEffect(() => {
      if (!enabled || !register) return;

      const id = idRef.current;
      register(id, keyMap, { preventDefault, allowWhileTyping });

      return () => {
         if (unregister) {
            unregister(id);
         }
      };
   }, [enabled, keyMap, register, unregister, preventDefault, allowWhileTyping]);
};

export default useKeyboardNavigation;
